import { useMemo, useState, type CSSProperties } from 'react'
import type { HealthCategory } from '../../../data/ServerApiClient'

// Per-category weight editor: shows WHY the AI weighted these sub-metrics (its note), then every scorable
// metric in the category with a relative-importance slider (AI dot = the AI's pick, thumb = the current /
// user value). The user can raise a metric the AI left at 0 to bring it into the score, or drop a metric
// to 0 to exclude it. Weights are RELATIVE within the category — the engine renormalizes — so there's no
// "must total 100%" constraint here (that only applies to the five top-level categories).
//
// Display is normalized so the strongest metric sits near 100 (raw AI weights vary in scale per company:
// some are 0.5/0.35, others 3/2). On save we send the slider values straight through as ratio_weights;
// only their ratios matter to the engine.

export type CfoCategoryWeightEditorProps = {
  category: HealthCategory
  /** Persist this category's per-metric weights (metric_id → relative weight; 0 excludes it). */
  onSave: (ratioWeights: Record<string, number>) => Promise<void> | void
  busy?: boolean
}

export function CfoCategoryWeightEditor({ category, onSave, busy }: CfoCategoryWeightEditorProps) {
  const [open, setOpen] = useState(false)

  // Metric label lookup (the scored cards carry the labels).
  const labels = useMemo(() => {
    const m: Record<string, string> = {}
    for (const r of category.ratios) m[r.id] = r.label
    return m
  }, [category.ratios])

  // Every scorable metric in this category, in the server's key-first order (weighted metrics first).
  const metricIds = useMemo(() => {
    const ids = category.ratios.map((r) => r.id)
    for (const id of Object.keys(category.ratio_weights ?? {})) if (!ids.includes(id)) ids.push(id)
    return ids
  }, [category.ratios, category.ratio_weights])

  const effectiveRaw = category.ratio_weights ?? {}
  const aiRaw = category.ai_ratio_weights ?? effectiveRaw

  // One shared factor so thumb and AI dot are on the same 0..100 scale.
  const factor = useMemo(() => {
    let max = 0
    for (const id of metricIds) {
      max = Math.max(max, effectiveRaw[id] ?? 0, aiRaw[id] ?? 0)
    }
    return max > 0 ? 100 / max : 1
  }, [metricIds, effectiveRaw, aiRaw])

  const displayEffective = useMemo(() => {
    const m: Record<string, number> = {}
    for (const id of metricIds) m[id] = Math.round((effectiveRaw[id] ?? 0) * factor)
    return m
  }, [metricIds, effectiveRaw, factor])
  const displayAI = useMemo(() => {
    const m: Record<string, number> = {}
    for (const id of metricIds) m[id] = Math.round((aiRaw[id] ?? 0) * factor)
    return m
  }, [metricIds, aiRaw, factor])

  const [draft, setDraft] = useState<Record<string, number>>(displayEffective)
  const changed = metricIds.some((id) => (draft[id] ?? 0) !== (displayEffective[id] ?? 0))
  const anyWeighted = metricIds.some((id) => (draft[id] ?? 0) > 0)
  const userOverridden = !!category.ai_ratio_weights

  const openEditor = () => {
    setDraft(displayEffective)
    setOpen(true)
  }
  const save = async () => {
    if (!changed || !anyWeighted) return
    const out: Record<string, number> = {}
    for (const id of metricIds) out[id] = draft[id] ?? 0
    await onSave(out)
    setOpen(false)
  }

  return (
    <div style={{ marginTop: 4 }}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openEditor())}
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 12,
          fontWeight: 600,
          padding: '4px 10px',
          borderRadius: 999,
          border: '1px solid var(--hair-strong, #d4d8de)',
          background: 'var(--surface, #fff)',
          color: 'var(--brand, #1b4965)',
          cursor: 'pointer',
        }}
      >
        <span aria-hidden="true">✦</span>
        {userOverridden ? 'Metric weights · edited' : 'Why these metrics · adjust'}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={`Adjust ${category.label} metric weights`}
          style={{
            marginTop: 8,
            padding: 14,
            borderRadius: 12,
            border: '1px solid var(--hair, #e3e6ea)',
            background: 'var(--surface, #fff)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
          }}
        >
          {/* WHY — the AI's per-category note. */}
          <p style={{ margin: '0 0 12px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-2, #4c5360)' }}>
            {category.rationale ||
              'The strongest-signal metrics carry the weight for this category; the rest are shown for context but left out of the score.'}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {metricIds.map((id) => {
              const val = draft[id] ?? 0
              const ai = displayAI[id] ?? 0
              const excluded = val <= 0
              return (
                <div key={id} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 34px', gap: 8, alignItems: 'center' }}>
                  <span
                    style={{
                      fontSize: 12,
                      color: excluded ? 'var(--muted, #9aa0a8)' : 'var(--ink-2, #4c5360)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={labels[id] ?? id}
                  >
                    {labels[id] ?? id}
                  </span>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={val}
                      disabled={busy}
                      onChange={(e) => setDraft((d) => ({ ...d, [id]: Number(e.target.value) }))}
                      style={{ width: '100%', display: 'block' }}
                      aria-label={`${labels[id] ?? id} weight`}
                    />
                    {ai > 0 ? (
                      <span
                        title={`AI weight`}
                        style={{
                          position: 'absolute',
                          left: `${ai}%`,
                          bottom: -3,
                          transform: 'translateX(-50%)',
                          width: 7,
                          height: 7,
                          borderRadius: 999,
                          background: 'var(--brand, #1b4965)',
                          border: '1px solid var(--surface, #fff)',
                        }}
                      />
                    ) : null}
                  </div>
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: excluded ? 'var(--muted, #9aa0a8)' : 'var(--ink, #1b1f24)',
                      textAlign: 'right',
                    }}
                  >
                    {excluded ? '—' : val}
                  </span>
                </div>
              )
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
            <span style={{ fontSize: 11.5, color: 'var(--muted, #6b7280)', marginRight: 'auto' }}>
              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 999, background: 'var(--brand)', marginRight: 5, verticalAlign: 'middle' }} />
              AI weight · 0 = not scored
            </span>
            {changed ? (
              <button type="button" disabled={busy} onClick={() => setDraft(displayAI)} style={actionStyle('var(--ink-2, #3a3f46)')}>
                Reset to AI
              </button>
            ) : null}
            <button type="button" disabled={!changed || !anyWeighted || busy} onClick={save} style={actionStyle('var(--brand, #1b4965)', changed && anyWeighted && !busy)}>
              {anyWeighted ? 'Save weights' : 'Weight at least one'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const actionStyle = (color: string, enabled = true): CSSProperties => ({
  fontSize: 12,
  fontWeight: 600,
  padding: '6px 12px',
  borderRadius: 8,
  border: `1px solid ${enabled ? color : 'var(--hair-strong, #d4d8de)'}`,
  background: 'var(--surface, #fff)',
  color: enabled ? color : 'var(--muted, #9aa0a8)',
  cursor: enabled ? 'pointer' : 'not-allowed',
})

export default CfoCategoryWeightEditor
